"use client";

import { useEffect, useMemo } from "react";

import {
  TITRATION_CONFIG,
  type AmmeterPlacement,
  type ElectricalCircuitInput,
  type ExperimentId,
  type VoltmeterPlacement,
  acidConcentrationFromTitre,
  buretteReadingMl,
  makeTitrationTrial,
  meterDisplay,
  phenolphthaleinAppearance,
  selectConcordantTitres,
  solveFilamentCircuit,
  titrationPh,
} from "./models";
import styles from "./experiments.module.css";
import type {
  ElectricalSessionState,
  ExperimentSessionsState,
  RecordedElectricalPoint,
  TitrationSessionState,
} from "./session";

type SessionChange<T> = (update: (current: T) => T) => void;

type ExperimentRuntimeProps = {
  experimentId: ExperimentId;
  sessions: ExperimentSessionsState;
  onExit: () => void;
  onChangeExperiment: (experimentId: ExperimentId) => void;
  onUpdateTitration: SessionChange<TitrationSessionState>;
  onUpdateElectrical: SessionChange<ElectricalSessionState>;
};

function sessionFieldSetter<T, K extends keyof T>(
  onChange: SessionChange<T>,
  key: K
) {
  return (value: T[K] | ((current: T[K]) => T[K])) => {
    onChange((current) => {
      const nextValue =
        typeof value === "function"
          ? (value as (currentValue: T[K]) => T[K])(current[key])
          : value;
      return { ...current, [key]: nextValue };
    });
  };
}

const EXPERIMENT_META: Record<
  ExperimentId,
  { eyebrow: string; title: string; station: string }
> = {
  titration: {
    eyebrow: "Chemistry practical",
    title: "Acid–base titration",
    station: "Wet Lab bench",
  },
  electrical: {
    eyebrow: "Physics practical",
    title: "Filament-lamp I–V characteristic",
    station: "Dry Lab table",
  },
};

function format(value: number, decimals: number) {
  return value.toFixed(decimals);
}

export default function ExperimentRuntime({
  experimentId,
  sessions,
  onExit,
  onChangeExperiment,
  onUpdateTitration,
  onUpdateElectrical,
}: ExperimentRuntimeProps) {
  const meta = EXPERIMENT_META[experimentId];

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onExit();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onExit]);

  return (
    <section
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={meta.title}
    >
      <div className={styles.shell}>
        <header className={styles.header}>
          <div>
            <span className={styles.eyebrow}>{meta.eyebrow}</span>
            <h2>{meta.title}</h2>
            <p>{meta.station} · deterministic virtual practical</p>
          </div>
          <div className={styles.headerActions}>
            <div className={styles.experimentSwitch} aria-label="Experiment">
              <button
                type="button"
                className={
                  experimentId === "titration" ? styles.switchActive : ""
                }
                onClick={() => onChangeExperiment("titration")}
              >
                Chemistry
              </button>
              <button
                type="button"
                className={
                  experimentId === "electrical" ? styles.switchActive : ""
                }
                onClick={() => onChangeExperiment("electrical")}
              >
                Physics
              </button>
            </div>
            <button
              type="button"
              className={styles.exitButton}
              aria-label="Return to laboratory walk"
              onClick={onExit}
            >
              Return to lab
            </button>
          </div>
        </header>

        <div className={styles.body}>
          {experimentId === "titration" ? (
            <TitrationExperiment
              session={sessions.titration}
              onChange={onUpdateTitration}
            />
          ) : (
            <ElectricalExperiment
              session={sessions.electrical}
              onChange={onUpdateElectrical}
            />
          )}
        </div>
      </div>
    </section>
  );
}

function TitrationExperiment({
  session,
  onChange,
}: {
  session: TitrationSessionState;
  onChange: SessionChange<TitrationSessionState>;
}) {
  const {
    dispensedMl,
    airBubblePresent,
    eyeLevel,
    indicatorAdded,
    swirled,
    trials,
    answer,
    analysisFeedback,
  } = session;
  const setDispensedMl = sessionFieldSetter(onChange, "dispensedMl");
  const setAirBubblePresent = sessionFieldSetter(onChange, "airBubblePresent");
  const setEyeLevel = sessionFieldSetter(onChange, "eyeLevel");
  const setIndicatorAdded = sessionFieldSetter(onChange, "indicatorAdded");
  const setSwirled = sessionFieldSetter(onChange, "swirled");
  const setTrials = sessionFieldSetter(onChange, "trials");
  const setAnswer = sessionFieldSetter(onChange, "answer");
  const setAnalysisFeedback = sessionFieldSetter(onChange, "analysisFeedback");

  const ph = titrationPh(dispensedMl, airBubblePresent);
  const appearance = phenolphthaleinAppearance(ph);
  const finalReading = buretteReadingMl(dispensedMl, eyeLevel);
  const concordant = useMemo(
    () => selectConcordantTitres(trials.slice(1)),
    [trials]
  );

  function addBase(amountMl: number) {
    setDispensedMl((current) =>
      Math.min(24.8, Number((current + amountMl).toFixed(2)))
    );
    setSwirled(false);
  }

  function recordTrial() {
    if (!indicatorAdded || dispensedMl < 1) return;
    setTrials((current) => [
      ...current,
      makeTitrationTrial(dispensedMl, airBubblePresent, eyeLevel),
    ]);
    setDispensedMl(0);
    setAirBubblePresent(false);
    setSwirled(false);
    setAnalysisFeedback(null);
  }

  function checkConcentration() {
    if (!concordant) return;
    const parsed = Number(answer);
    if (!Number.isFinite(parsed)) {
      setAnalysisFeedback("Enter a concentration in mol dm⁻³.");
      return;
    }
    const expected = acidConcentrationFromTitre(concordant.meanMl);
    if (Math.abs(parsed - expected) <= 0.0005) {
      setAnalysisFeedback(
        "Good. Your result agrees with the mean titre. Hidden true concentration: " +
          format(TITRATION_CONFIG.acidConcentrationM, 4) +
          " mol dm⁻³."
      );
      return;
    }
    setAnalysisFeedback(
      "Recheck C(acid) × 25.00 = 0.1000 × " +
        format(concordant.meanMl, 2) +
        ". Expected about " +
        format(expected, 4) +
        " mol dm⁻³."
    );
  }

  return (
    <div className={styles.experimentGrid}>
      <section className={styles.apparatusPanel}>
        <div className={styles.panelHeading}>
          <div>
            <span className={styles.stepLabel}>Apparatus</span>
            <h3>Titrate the unknown HCl</h3>
          </div>
          <div className={styles.readout}>
            <strong>{format(finalReading, 2)} mL</strong>
            <span>burette reading</span>
          </div>
        </div>

        <div className={styles.titrationStage}>
          <div className={styles.buretteAssembly} aria-label="Burette">
            <div className={styles.buretteScale}>
              <span>0</span>
              <span>10</span>
              <span>20</span>
              <span>25</span>
            </div>
            <div className={styles.buretteTube}>
              <div
                className={styles.buretteLiquid}
                style={{
                  height:
                    String(Math.max(4, 100 - (dispensedMl / 25) * 96)) + "%",
                }}
              />
            </div>
            <div className={styles.buretteTap} />
            <div className={styles.buretteTip}>
              {airBubblePresent ? (
                <span
                  className={styles.airBubble}
                  title="Air bubble in burette tip"
                />
              ) : null}
            </div>
          </div>

          <div className={styles.flaskArea}>
            <div className={styles.flaskNeck} />
            <div
              className={styles.flask}
              style={{
                background:
                  indicatorAdded && swirled
                    ? appearance.css
                    : "rgba(248, 249, 247, 0.24)",
              }}
            >
              <span>
                {indicatorAdded
                  ? swirled
                    ? appearance.label
                    : "swirl to mix"
                  : "add indicator"}
              </span>
            </div>
            <div className={styles.whiteTile}>white tile</div>
          </div>
        </div>
        <div className={styles.controlGroups}>
          <div className={styles.controlGroup}>
            <span>Prepare</span>
            <button
              type="button"
              onClick={() => setAirBubblePresent(false)}
              disabled={!airBubblePresent || dispensedMl > 0}
            >
              Remove tip bubble
            </button>
            <button
              type="button"
              onClick={() => setIndicatorAdded(true)}
              disabled={indicatorAdded}
            >
              Add phenolphthalein
            </button>
          </div>

          <div className={styles.controlGroup}>
            <span>Add NaOH</span>
            <button type="button" onClick={() => addBase(1)}>
              +1.00 mL
            </button>
            <button type="button" onClick={() => addBase(0.1)}>
              +0.10 mL
            </button>
            <button type="button" onClick={() => addBase(0.05)}>
              +0.05 mL
            </button>
          </div>

          <div className={styles.controlGroup}>
            <span>Observe</span>
            <button type="button" onClick={() => setSwirled(true)}>
              Swirl flask
            </button>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={eyeLevel}
                onChange={(event) => setEyeLevel(event.target.checked)}
              />
              Read meniscus at eye level
            </label>
          </div>
        </div>

        <div className={styles.liveScience}>
          <div>
            <span>Calculated pH</span>
            <strong>{format(ph, 2)}</strong>
          </div>
          <div>
            <span>NaOH dispensed</span>
            <strong>{format(dispensedMl, 2)} mL</strong>
          </div>
          <div>
            <span>Endpoint cue</span>
            <strong>
              {indicatorAdded && swirled ? appearance.label : "not observed"}
            </strong>
          </div>
        </div>

        <button
          type="button"
          className={styles.primaryButton}
          disabled={!indicatorAdded || dispensedMl < 1}
          onClick={recordTrial}
        >
          Record titre and refill
        </button>
      </section>

      <aside className={styles.notebookPanel}>
        <div className={styles.panelHeading}>
          <div>
            <span className={styles.stepLabel}>Lab notebook</span>
            <h3>Measurements & analysis</h3>
          </div>
        </div>

        <div className={styles.methodCard}>
          <strong>Given</strong>
          <p>25.00 mL unknown HCl · 0.1000 mol dm⁻³ NaOH · 1:1 reaction.</p>
        </div>

        <div className={styles.mistakeCard}>
          <strong>Technique state</strong>
          <p>
            {airBubblePresent
              ? "Air bubble still present: the first 0.25 mL fills the tip instead of entering the flask."
              : "Burette tip is filled with solution."}
          </p>
          <p>
            {eyeLevel
              ? "Meniscus is being read at eye level."
              : "Parallax adds +0.08 mL to the displayed final reading."}
          </p>
        </div>

        <div className={styles.trialTable}>
          <div className={styles.trialHeader}>
            <span>Run</span>
            <span>Titre / mL</span>
            <span>Technique</span>
          </div>
          {trials.length ? (
            trials.map((trial, index) => (
              <div
                className={styles.trialRow}
                key={String(index) + "-" + String(trial.titreMl)}
              >
                <span>{index === 0 ? "rough" : index}</span>
                <strong>{format(trial.titreMl, 2)}</strong>
                <span>
                  {trial.airBubblePresent
                    ? "tip bubble"
                    : !trial.eyeLevel
                    ? "parallax"
                    : trial.overshootMl > 0.1
                    ? "overshot"
                    : "clean"}
                </span>
              </div>
            ))
          ) : (
            <p className={styles.emptyState}>
              Record a rough titre, then repeat until two titres are within 0.10
              mL.
            </p>
          )}
        </div>

        {concordant ? (
          <div className={styles.analysisCard}>
            <span className={styles.stepLabel}>Analyse</span>
            <p>
              Closest concordant titres:{" "}
              <strong>
                {format(concordant.titres[0], 2)} and{" "}
                {format(concordant.titres[1], 2)} mL
              </strong>
              . Mean = <strong>{format(concordant.meanMl, 2)} mL</strong>.
            </p>
            <label>
              HCl concentration / mol dm⁻³
              <input
                type="number"
                aria-label="HCl concentration answer"
                step="0.0001"
                value={answer}
                onChange={(event) => setAnswer(event.target.value)}
                placeholder="0.0000"
              />
            </label>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={checkConcentration}
            >
              Check calculation
            </button>
            {analysisFeedback ? (
              <p className={styles.feedback}>{analysisFeedback}</p>
            ) : null}
          </div>
        ) : (
          <div className={styles.analysisCard}>
            <span className={styles.stepLabel}>Analyse</span>
            <p>
              You need two concordant titres before calculating the unknown
              concentration.
            </p>
          </div>
        )}
      </aside>
    </div>
  );
}

function ElectricalExperiment({
  session,
  onChange,
}: {
  session: ElectricalSessionState;
  onChange: SessionChange<ElectricalSessionState>;
}) {
  const {
    supplyVoltageV,
    seriesResistanceOhm,
    switchClosed,
    ammeterPlacement,
    voltmeterPlacement,
    ammeterRangeA,
    voltmeterRangeV,
    points,
    reason,
    analysisFeedback,
  } = session;
  const setSupplyVoltageV = sessionFieldSetter(onChange, "supplyVoltageV");
  const setSeriesResistanceOhm = sessionFieldSetter(
    onChange,
    "seriesResistanceOhm"
  );
  const setSwitchClosed = sessionFieldSetter(onChange, "switchClosed");
  const setAmmeterPlacement = sessionFieldSetter(onChange, "ammeterPlacement");
  const setVoltmeterPlacement = sessionFieldSetter(
    onChange,
    "voltmeterPlacement"
  );
  const setAmmeterRangeA = sessionFieldSetter(onChange, "ammeterRangeA");
  const setVoltmeterRangeV = sessionFieldSetter(onChange, "voltmeterRangeV");
  const setPoints = sessionFieldSetter(onChange, "points");
  const setReason = sessionFieldSetter(onChange, "reason");
  const setAnalysisFeedback = sessionFieldSetter(onChange, "analysisFeedback");

  const input: ElectricalCircuitInput = {
    supplyVoltageV,
    seriesResistanceOhm,
    switchClosed,
    ammeterPlacement,
    voltmeterPlacement,
  };
  const reading = solveFilamentCircuit(input);
  const currentDisplay = reading.valid
    ? meterDisplay(
        reading.currentA,
        ammeterRangeA,
        ammeterRangeA === 0.2 ? 0.0001 : 0.001,
        ammeterRangeA === 0.2 ? 4 : 3
      )
    : "ERR";
  const voltageDisplay = reading.valid
    ? meterDisplay(
        reading.lampVoltageV,
        voltmeterRangeV,
        voltmeterRangeV === 2 ? 0.001 : 0.01,
        voltmeterRangeV === 2 ? 3 : 2
      )
    : "ERR";

  function recordPoint() {
    if (
      !switchClosed ||
      !reading.valid ||
      currentDisplay === "OL" ||
      voltageDisplay === "OL" ||
      reading.currentA <= 0
    ) {
      return;
    }
    setPoints((current) => [
      ...current,
      {
        lampVoltageV: Number(reading.lampVoltageV.toFixed(3)),
        currentA: Number(reading.currentA.toFixed(4)),
        resistanceOhm: Number(reading.lampResistanceOhm.toFixed(2)),
      },
    ]);
  }

  function checkReason() {
    setAnalysisFeedback(
      reason === "heating"
        ? "Correct. Increasing current heats the filament. Its resistance rises, so current increases less rapidly as voltage increases."
        : "Look for the causal chain: current changes filament temperature, which changes resistance."
    );
  }

  return (
    <div className={styles.experimentGrid}>
      <section className={styles.apparatusPanel}>
        <div className={styles.panelHeading}>
          <div>
            <span className={styles.stepLabel}>Circuit bench</span>
            <h3>Build and measure the lamp circuit</h3>
          </div>
          <div className={styles.lampStatus}>
            <span
              className={styles.lampBulb}
              style={{
                opacity: Math.min(1, 0.18 + (reading.lampPowerW / 2.5) * 0.82),
              }}
            />
            <strong>
              {switchClosed && reading.valid
                ? String(Math.round(reading.filamentTemperatureC)) + " °C"
                : "lamp off"}
            </strong>
          </div>
        </div>
        <div className={styles.circuitBoard}>
          <div className={[styles.component, styles.supply].join(" ")}>
            <span>DC supply</span>
            <strong>{format(supplyVoltageV, 1)} V</strong>
          </div>
          <div className={[styles.component, styles.ammeter].join(" ")}>
            <span>A</span>
            <strong>{currentDisplay}</strong>
          </div>
          <div className={[styles.component, styles.resistor].join(" ")}>
            <span>variable R</span>
            <strong>{format(seriesResistanceOhm, 1)} Ω</strong>
          </div>
          <div className={[styles.component, styles.lamp].join(" ")}>
            <span>lamp</span>
            <strong>{format(reading.lampResistanceOhm, 1)} Ω</strong>
          </div>
          <div className={[styles.component, styles.voltmeter].join(" ")}>
            <span>V</span>
            <strong>{voltageDisplay}</strong>
          </div>
          <div
            className={[
              styles.circuitWire,
              reading.valid ? styles.wireHealthy : styles.wireError,
            ].join(" ")}
            aria-hidden="true"
          />
        </div>

        {reading.error ? (
          <div className={styles.circuitError}>{reading.error}</div>
        ) : null}

        <div className={styles.wiringControls}>
          <label>
            Ammeter
            <select
              aria-label="Ammeter placement"
              value={ammeterPlacement}
              onChange={(event) =>
                setAmmeterPlacement(event.target.value as AmmeterPlacement)
              }
            >
              <option value="series">in series</option>
              <option value="parallel">across supply (wrong)</option>
            </select>
          </label>
          <label>
            Voltmeter
            <select
              aria-label="Voltmeter placement"
              value={voltmeterPlacement}
              onChange={(event) =>
                setVoltmeterPlacement(event.target.value as VoltmeterPlacement)
              }
            >
              <option value="across-lamp">across lamp</option>
              <option value="series">in series (wrong)</option>
            </select>
          </label>
          <button
            type="button"
            className={switchClosed ? styles.switchClosed : ""}
            onClick={() => setSwitchClosed((current) => !current)}
          >
            Switch: {switchClosed ? "closed" : "open"}
          </button>
        </div>

        <div className={styles.sliderGrid}>
          <label>
            Supply voltage
            <input
              type="range"
              aria-label="Supply voltage"
              min="0.5"
              max="6"
              step="0.5"
              value={supplyVoltageV}
              onChange={(event) =>
                setSupplyVoltageV(Number(event.target.value))
              }
            />
            <strong>{format(supplyVoltageV, 1)} V</strong>
          </label>
          <label>
            Variable resistance
            <input
              type="range"
              aria-label="Variable resistance"
              min="0"
              max="20"
              step="1"
              value={seriesResistanceOhm}
              onChange={(event) =>
                setSeriesResistanceOhm(Number(event.target.value))
              }
            />
            <strong>{format(seriesResistanceOhm, 0)} Ω</strong>
          </label>
        </div>

        <div className={styles.meterRanges}>
          <label>
            Ammeter range
            <select
              aria-label="Ammeter range"
              value={ammeterRangeA}
              onChange={(event) => setAmmeterRangeA(Number(event.target.value))}
            >
              <option value="0.2">0.200 A</option>
              <option value="2">2.000 A</option>
            </select>
          </label>
          <label>
            Voltmeter range
            <select
              aria-label="Voltmeter range"
              value={voltmeterRangeV}
              onChange={(event) =>
                setVoltmeterRangeV(Number(event.target.value))
              }
            >
              <option value="2">2.000 V</option>
              <option value="20">20.00 V</option>
            </select>
          </label>
        </div>

        <div className={styles.liveScience}>
          <div>
            <span>Lamp voltage</span>
            <strong>{format(reading.lampVoltageV, 3)} V</strong>
          </div>
          <div>
            <span>Current</span>
            <strong>{format(reading.currentA, 4)} A</strong>
          </div>
          <div>
            <span>Lamp resistance</span>
            <strong>{format(reading.lampResistanceOhm, 2)} Ω</strong>
          </div>
        </div>

        <button
          type="button"
          className={styles.primaryButton}
          disabled={
            !switchClosed ||
            !reading.valid ||
            currentDisplay === "OL" ||
            voltageDisplay === "OL" ||
            reading.currentA <= 0
          }
          onClick={recordPoint}
        >
          Record V–I point
        </button>
      </section>

      <aside className={styles.notebookPanel}>
        <div className={styles.panelHeading}>
          <div>
            <span className={styles.stepLabel}>Lab notebook</span>
            <h3>I–V measurements</h3>
          </div>
        </div>

        <div className={styles.methodCard}>
          <strong>Goal</strong>
          <p>
            Collect at least five lamp-voltage/current pairs across a useful
            range, then explain why the graph curves.
          </p>
        </div>

        <div className={styles.trialTable}>
          <div className={styles.electricalHeader}>
            <span>Point</span>
            <span>V / V</span>
            <span>I / A</span>
            <span>R / Ω</span>
          </div>
          {points.length ? (
            points.map((point, index) => (
              <div
                className={styles.electricalRow}
                key={String(index) + "-" + String(point.currentA)}
              >
                <span>{index + 1}</span>
                <strong>{format(point.lampVoltageV, 3)}</strong>
                <strong>{format(point.currentA, 4)}</strong>
                <span>{format(point.resistanceOhm, 2)}</span>
              </div>
            ))
          ) : (
            <p className={styles.emptyState}>
              Correct the wiring, close the switch, choose suitable meter
              ranges, then record your first point.
            </p>
          )}
        </div>

        <MiniIvGraph points={points} />

        <div className={styles.analysisCard}>
          <span className={styles.stepLabel}>Analyse</span>
          <p>Why does the filament-lamp I–V graph become less steep?</p>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="iv-reason"
              value="heating"
              checked={reason === "heating"}
              onChange={(event) => setReason(event.target.value)}
            />
            The filament heats up, so its resistance increases.
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="iv-reason"
              value="charge"
              checked={reason === "charge"}
              onChange={(event) => setReason(event.target.value)}
            />
            Charge is used up as it passes through the lamp.
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="iv-reason"
              value="meter"
              checked={reason === "meter"}
              onChange={(event) => setReason(event.target.value)}
            />
            The voltmeter removes current from the circuit.
          </label>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={checkReason}
            disabled={!reason || points.length < 3}
          >
            Check explanation
          </button>
          {points.length < 5 ? (
            <p className={styles.hint}>
              {5 - points.length} more measurement
              {5 - points.length === 1 ? "" : "s"} recommended.
            </p>
          ) : null}
          {analysisFeedback ? (
            <p className={styles.feedback}>{analysisFeedback}</p>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function MiniIvGraph({ points }: { points: RecordedElectricalPoint[] }) {
  const width = 320;
  const height = 190;
  const padding = 28;
  const maxV = Math.max(5, ...points.map((point) => point.lampVoltageV));
  const maxI = Math.max(0.5, ...points.map((point) => point.currentA));
  const plotted = points.map((point) => ({
    x: padding + (point.lampVoltageV / maxV) * (width - padding * 2),
    y: height - padding - (point.currentA / maxI) * (height - padding * 2),
  }));

  return (
    <figure className={styles.graphCard}>
      <figcaption>I–V plot</figcaption>
      <svg
        viewBox={"0 0 " + String(width) + " " + String(height)}
        role="img"
        aria-label="Recorded filament lamp current against voltage"
      >
        <line
          x1={padding}
          y1={height - padding}
          x2={width - padding / 2}
          y2={height - padding}
          className={styles.graphAxis}
        />
        <line
          x1={padding}
          y1={height - padding}
          x2={padding}
          y2={padding / 2}
          className={styles.graphAxis}
        />
        {plotted.length > 1 ? (
          <polyline
            points={plotted
              .map((point) => String(point.x) + "," + String(point.y))
              .join(" ")}
            className={styles.graphLine}
          />
        ) : null}
        {plotted.map((point, index) => (
          <circle
            key={String(point.x) + "-" + String(point.y) + "-" + String(index)}
            cx={point.x}
            cy={point.y}
            r="4"
            className={styles.graphPoint}
          />
        ))}
        <text x={width - 43} y={height - 8} className={styles.graphLabel}>
          V / V
        </text>
        <text x="4" y="19" className={styles.graphLabel}>
          I / A
        </text>
      </svg>
    </figure>
  );
}
